<template>
  <MChart
    ref="chart"
    :series="metricSeries"
    :options="chartOptions"
    :height="height"
  />
</template>

<script>
  import formatNumber from 'legacy/modules/common/components/MNumeric/formatNumber';
  import MChart from 'legacy/modules/common/components/MChart/MChart';
  import groupBy from 'lodash.groupby';
  import moment from 'moment-wrapper';
  import periodsComparisonWidgetTooltip from 'components/Dashboards/Widgets/PeriodsComparisonWidget/periodsComparisonWidgetTooltip';
  import widgetColors from 'utils/design/chartColors';

  const AXIS_TEXT_COLOR = '#8D8D8D';

  export default {
    name: 'PeriodsComparisonWidgetChart',
    components: {
      MChart
    },
    props: {
      statistics: {
        type: Array,
        default: Array
      },
      metric: {
        type: String,
        default: ''
      },
      grouping: {
        type: String,
        default: ''
      },
      metricType: {
        type: String,
        default: 'integer'
      },
      height: {
        type: String,
        default: '100%'
      },
      comparisonPeriods: {
        type: Object,
        default: Object
      }
    },
    computed: {
      metricSeries () {
        const groupedByPeriod = groupBy(this.statistics.filter(x => x.periodToCompare), 'periodToCompare');
        const groupedByPeriodKeys = Object.keys(groupedByPeriod);

        return groupedByPeriodKeys.map(i => {
          let allDays = [];
          let date = moment(this.comparisonPeriods[i].dateStart).formatDateApi();

          while (!moment(date).isAfter(moment(this.comparisonPeriods[i].dateEnd))) {
            allDays.push(date);
            date = moment(date).add(1, 'days').
              formatDateApi();
          }
          const groupedByDay = groupBy(groupedByPeriod[i], 'date');
          const data = allDays.map(el => ({
            x: moment(el).formatDate(),
            y: groupedByDay[el] ? Number(groupedByDay[el][0][this.metric]) : 0
          }));

          return {
            name: this.periods[i],
            data
          };
        });
      },
      categories () {
        let result = [];
        let totalDays = 0;
        let dayOfWeek = '';

        if (!this.comparisonPeriods.firstPeriod.dateStart || !this.comparisonPeriods.secondPeriod.dateStart) {
          let period = this.comparisonPeriods.firstPeriod.dateStart ? this.comparisonPeriods.firstPeriod
            : this.comparisonPeriods.secondPeriod;

          const start = moment(period.dateStart);
          totalDays = period.totalDays;

          dayOfWeek = start.formatDateApi();
        } else {
          const firstPeriodStart = moment(this.comparisonPeriods.firstPeriod.dateStart);
          const secondPeriodStart = moment(this.comparisonPeriods.secondPeriod.dateStart);

          if (firstPeriodStart.day() === secondPeriodStart.day()) {
            totalDays =
              Math.max(this.comparisonPeriods.firstPeriod.totalDays, this.comparisonPeriods.secondPeriod.totalDays);

            dayOfWeek = firstPeriodStart.formatDateApi();
          }
        }

        for (let i = 0; i < totalDays; i++) {
          result.push(moment(dayOfWeek).add(i, 'days').
            format('dd').
            toLowerCase());
        }

        return result;
      },
      periods () {
        let firstPeriod = null;
        let secondPeriod = null;

        if (this.comparisonPeriods.firstPeriod.dateStart) {
          firstPeriod = `${moment(this.comparisonPeriods.firstPeriod.dateStart).formatDate()} - ` +
            `${moment(this.comparisonPeriods.firstPeriod.dateEnd).formatDate()}`;
        }

        if (this.comparisonPeriods.secondPeriod.dateStart) {
          secondPeriod = `${moment(this.comparisonPeriods.secondPeriod.dateStart).formatDate()} - ` +
            `${moment(this.comparisonPeriods.secondPeriod.dateEnd).formatDate()}`;
        }

        return {
          firstPeriod,
          secondPeriod
        };
      },
      chartOptions () {
        return {
          colors: widgetColors(),
          grid: {
            padding: {
              top: -20,
              right: 8,
              bottom: 0,
              left: -5
            }
          },
          chart: {
            type: 'bar',
            toolbar: {
              show: false
            },
            zoom: {
              enabled: false
            }
          },
          dataLabels: {
            enabled: false
          },
          legend: {
            show: true,
            showForSingleSeries: true,
            showForNullSeries: true,
            showForZeroSeries: true,
            position: 'bottom',
            markers: {
              colors: ['#FEB019', '#00E396'],
              radius: 50
            },
            onItemClick: {
              toggleDataSeries: false
            },
            onItemHover: {
              highlightDataSeries: false
            },
            itemMargin: {
              horizontal: 4,
              vertical: 8
            }
          },
          stroke: {
            show: true,
            width: 2,
            colors: ['transparent']
          },
          tooltip: {
            custom: periodsComparisonWidgetTooltip(this.formatter.bind(this)),
            y: {
              formatter: (val) => formatNumber(val, this.metricType, {withCurrencySymbol: true})
            },
            x: {
              show: false
            }
          },
          yaxis: {
            show: true,

            // floating: true,
            showAlways: true,
            labels: {
              show: true,
              style: {
                color: AXIS_TEXT_COLOR,
                fontSize: '12px'
              },
              offsetX: -5,
              formatter: x => formatNumber(x, this.metricType)
            }
          },
          xaxis: {
            categories: this.categories,
            labels: {
              show: this.categories.length
            }
          }
        };
      }
    },
    methods: {
      formatter (val) {
        return formatNumber(val, this.metricType, {withCurrencySymbol: true});
      }
    }
  };
</script>
